import { useCallback, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import {
  useCheckinBooking,
  useCheckoutBooking,
  useArriveBooking,
  useLeaveBooking,
  useDeleteBooking,
  useApproveBooking,
  useRefuseBooking,
  useUpdateParticipationBooking,
  useCreateBooking,
} from '../../mutations';
import { useDeleteBookings, useUpdateBooking } from '@jooxter/bookings';
import {
  IJxtEventSummary,
  JxtEventDate,
  JxtEventSummary,
  JxtDeleteEventModal,
  JxtLoader,
  JxtRadioButton,
  JxtResourceInfos,
  generateToast,
} from '@jooxter/ui';
import { useTranslation } from 'react-i18next';
import {
  useInvalidateBookingQueries,
  useInvalidateCorporationQueries,
  useInvalidateResourceQuotaQueries,
} from '../../queries';
import { FloorQueryKeys } from '@jooxter/floors';
import { useInvalidateWorkplaceQueries } from '@jooxter/workplaces';
import { useOffCanvasStore } from '../../store';
import { useQueryClient } from '@tanstack/react-query';
import {
  fromISO,
  ISOToLocalDate,
  ISOToLocalTime,
  JxtBookingParticipationStatusEnum,
  JxtResourcesTypeEnum,
  RecurrentBookingClosingResultEnum,
} from '@jooxter/utils';
import {
  Booking,
  BookingAttendeeParticipationStatusEnum,
  BookingRequest,
  RecurrenceDto,
  ResourceTypeDto,
} from '@jooxter/api';
import { defaultOnError } from '../../mutations/default-error';
import { useForm } from 'react-hook-form';

export interface IJxtDeleteBookingInformations {
  id: number;
  rrule?: string;
  summary: IJxtEventSummary;
  start: {
    dateTime: string;
  };
  end: {
    dateTime: string;
  };
  resourceName: string;
  resourceType?: ResourceTypeDto;
}

export const useBookingActions = (onBookingDelete?: (id: number) => void) => {
  // @TODO : see when react-hook-form properly supports react-compiler
  // https://github.com/orgs/react-hook-form/discussions/12524
  // https://jooxter.atlassian.net/browse/ND-6018
  'use no memo';
  const { t } = useTranslation();
  const close = useOffCanvasStore((state) => state.close);
  const queryClient = useQueryClient();
  const { invalidateBookingQueries } = useInvalidateBookingQueries();
  const { invalidateWorkplaceQueries } = useInvalidateWorkplaceQueries();
  const { invalidateCorporationQueries } = useInvalidateCorporationQueries();
  const { invalidateResourceQuotaQueries } = useInvalidateResourceQuotaQueries();
  const { mutate: mutationCheckin } = useCheckinBooking();
  const { mutate: mutationCheckout } = useCheckoutBooking();
  const { mutate: mutationRefuse } = useRefuseBooking((data: Booking) => invalidateBookingQueries([data.id]));
  const { mutate: mutationArrive } = useArriveBooking();
  const { mutate: mutationLeave } = useLeaveBooking();
  const { mutate: mutationDelete } = useDeleteBooking();
  const { mutate: mutationUpdate } = useUpdateBooking();
  const { mutateAsync: mutationCreate } = useCreateBooking();
  const { mutate: mutationDeleteMultiple } = useDeleteBookings((_: Booking, variables: number[]) =>
    invalidateBookingQueries(variables)
  );
  const [isBookingDeleting, setIsBookingDeleting] = useState<boolean>(false);
  const { mutateAsync: mutationDeleteRecurrences } = useDeleteBooking(true);
  const { mutate: mutationApprove } = useApproveBooking();
  const { mutate: mutationUpdateParticipation } = useUpdateParticipationBooking();
  const [showDeleteBookingModal, setShowDeleteBookingModal] = useState<boolean>(false);
  const [bookingToDeleteInformation, setBookingToDeleteInformation] = useState<IJxtDeleteBookingInformations>();
  const { register, handleSubmit } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      recurrence: RecurrentBookingClosingResultEnum.ONLY_SELF,
    },
  });

  const deleteBookings = useCallback(
    (ids: number[]) => {
      return mutationDeleteMultiple(ids);
    },
    [mutationDeleteMultiple]
  );

  const update = useCallback(
    (
      { id, payload }: { id: string | number; payload: BookingRequest },
      onSuccess?: (booking: Booking) => void,
      onError?: (error: unknown) => void,
      onSettled?: () => void
    ) => {
      const idInt = typeof id === 'string' ? parseInt(id, 10) : id;

      const success = (booking: Booking) => {
        generateToast(
          t('update-saved'),
          false,
          t<string>('booking-updated-message', {
            resourceName: booking.resource.name,
            bookingDate: ISOToLocalDate(booking.start.dateTime, booking.resource.timezone),
            startTime: ISOToLocalTime(booking.start.dateTime, booking.resource.timezone),
            endTime: ISOToLocalTime(booking.end.dateTime, booking.resource.timezone),
          })
        );
        invalidateBookingQueries([idInt]);
      };

      const error = (error: AxiosError) => {
        const axiosError = error;
        const response = axiosError.response?.data as { error: string; description: string }[];
        generateToast(t('booking-update-failed'), true, response.length > 0 ? response[0].error : undefined);
      };

      return mutationUpdate(
        { id: idInt, payload },
        {
          onSuccess: onSuccess || success,
          onError: onError || error,
          onSettled,
        }
      );
    },
    [invalidateBookingQueries, mutationUpdate, t]
  );

  const create = useCallback(
    (
      bookingRequest: BookingRequest,
      onSuccess?: (booking: Booking | RecurrenceDto) => void,
      onError?: (error: AxiosError, variables: BookingRequest) => void,
      onSettled?: () => void
    ) => {
      const error = (e: AxiosError) => {
        defaultOnError(e);
      };

      return mutationCreate(bookingRequest, {
        onSuccess: onSuccess,
        onError: onError || error,
        onSettled,
      });
    },
    [mutationCreate]
  );

  const checkin = useCallback(
    (id: string | number, isZone = false) => {
      const idInt = typeof id === 'string' ? parseInt(id, 10) : id;

      const success = () => {
        generateToast(t('booking-checkin-success'), false);
        invalidateBookingQueries([idInt]);
      };

      const error = () => {
        generateToast(t('booking-checkin-failed'), true);
      };

      if (isZone) {
        return mutationArrive(
          { id: idInt },
          {
            onSuccess: success,
            onError: error,
          }
        );
      }

      return mutationCheckin(
        { id: idInt },
        {
          onSuccess: success,
          onError: error,
        }
      );
    },
    [invalidateBookingQueries, mutationCheckin, mutationArrive, t]
  );

  const checkout = useCallback(
    (id: string | number, isZone = false) => {
      const idInt = typeof id === 'string' ? parseInt(id, 10) : id;

      const success = () => {
        invalidateBookingQueries([idInt]);
        generateToast(t('booking-checkout-success'), false);
      };

      const error = () => {
        generateToast(t('booking-checkout-failed'), true);
      };

      if (isZone) {
        return mutationLeave(
          { id: idInt },
          {
            onSuccess: success,
            onError: error,
          }
        );
      }

      return mutationCheckout(
        { id: idInt },
        {
          onSuccess: success,
          onError: error,
        }
      );
    },
    [invalidateBookingQueries, mutationCheckout, mutationLeave, t]
  );

  const arrive = useCallback((id: string | number) => checkin(id, true), [checkin]);
  const leave = useCallback((id: string | number) => checkout(id, true), [checkout]);

  const approve = useCallback(
    (id: string | number) => {
      const idInt = typeof id === 'string' ? parseInt(id, 10) : id;

      const success = () => {
        invalidateBookingQueries([idInt]);
        generateToast(
          t('booking-validation.approved-success'),
          false,
          t<string>('booking-validation.approved-success-subtitle')
        );
      };

      const error = () => {
        generateToast(t('booking-validation-failed'), true);
      };

      return mutationApprove(
        { id: idInt },
        {
          onSuccess: success,
          onError: error,
        }
      );
    },
    [mutationApprove, invalidateBookingQueries, t]
  );

  const refuse = useCallback(
    (id: string | number) => {
      const idInt = typeof id === 'string' ? parseInt(id, 10) : id;

      const success = () => {
        invalidateBookingQueries([idInt]);
        generateToast(
          t('booking-validation.refused-success'),
          false,
          t<string>('booking-validation.refused-success-subtitle')
        );
      };

      const error = () => {
        generateToast(t('booking-validation-failed'), true);
      };

      return mutationRefuse(
        { id: idInt },
        {
          onSuccess: success,
          onError: error,
        }
      );
    },
    [mutationRefuse, invalidateBookingQueries, t]
  );

  const cancel = useCallback((booking?: IJxtDeleteBookingInformations): void => {
    if (booking) {
      setBookingToDeleteInformation(booking);
      setShowDeleteBookingModal(true);
    }
  }, []);

  const updateParticipationStatus = useCallback(
    (id: string | number, status: JxtBookingParticipationStatusEnum) => {
      const idInt = typeof id === 'string' ? parseInt(id, 10) : id;

      const success = () => {
        invalidateBookingQueries([idInt]);
        switch (status) {
          case JxtBookingParticipationStatusEnum.Accepted:
            generateToast(
              t('booking-participation.status.yes-success'),
              false,
              t<string>('booking-participation.status.yes-success-subtitle')
            );
            break;
          case JxtBookingParticipationStatusEnum.AcceptedRemotely:
            generateToast(
              t('booking-participation.status.remotely-success'),
              false,
              t<string>('booking-participation.status.remotely-success-subtitle')
            );
            break;
          case JxtBookingParticipationStatusEnum.Declined:
            generateToast(
              t('booking-participation.status.no-success'),
              false,
              t<string>('booking-participation.status.no-success-subtitle')
            );
            break;
        }
      };

      const error = () => {
        generateToast(t('booking-participation.status-failed'), true);
      };

      mutationUpdateParticipation(
        { id: idInt, status: status as unknown as BookingAttendeeParticipationStatusEnum },
        {
          onSuccess: success,
          onError: error,
        }
      );
    },
    [mutationUpdateParticipation, invalidateBookingQueries, t]
  );

  const closeDeleteBookingModal = useCallback(() => {
    setShowDeleteBookingModal(false);
    close();
  }, [close]);

  const onConfirmDeletion = useCallback(
    (id: number, recurrence: boolean) => {
      const onSuccess = () => {
        queryClient.invalidateQueries({
          queryKey: [FloorQueryKeys.GetFloorPlanResources],
        });
        invalidateWorkplaceQueries();
        generateToast(
          recurrence ? t<string>('booking-and-following-occurrences-cancelled') : t<string>('booking-cancelled'),
          false
        );

        if (onBookingDelete) {
          onBookingDelete(id);
        }
      };
      const onError = () => {
        generateToast(t<string>('booking-cancellation-failed'), true);
      };

      const onSettled = () => {
        invalidateBookingQueries([id]);
        invalidateCorporationQueries();
        invalidateResourceQuotaQueries();
      };

      if (recurrence) {
        setIsBookingDeleting(true);
        mutationDeleteRecurrences(
          { id },
          {
            onSuccess,
            onError,
            onSettled: () => {
              setIsBookingDeleting(false);
              closeDeleteBookingModal();
              onSettled();
            },
          }
        );
      } else {
        closeDeleteBookingModal();
        mutationDelete(
          { id },
          {
            onSuccess,
            onError,
            onSettled,
          }
        );
      }
    },
    [
      invalidateBookingQueries,
      invalidateWorkplaceQueries,
      invalidateCorporationQueries,
      invalidateResourceQuotaQueries,
      mutationDelete,
      mutationDeleteRecurrences,
      onBookingDelete,
      queryClient,
      closeDeleteBookingModal,
      t,
    ]
  );
  const onSubmit = useCallback(
    (e: { recurrence: RecurrentBookingClosingResultEnum }) => {
      if (e && 'recurrence' in e && bookingToDeleteInformation?.id) {
        onConfirmDeletion(bookingToDeleteInformation?.id, e.recurrence === RecurrentBookingClosingResultEnum.FOLLOWING);
      } else {
        closeDeleteBookingModal();
      }
    },
    [bookingToDeleteInformation?.id, onConfirmDeletion, closeDeleteBookingModal]
  );

  const directCancel = useCallback(
    (id: string | number) => {
      onConfirmDeletion(typeof id === 'string' ? parseInt(id, 10) : id, false);
    },
    [onConfirmDeletion]
  );

  const deleteBookingModal = useMemo(
    () => (
      <JxtDeleteEventModal
        titleKey="do-you-want-to-delete-this-booking"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeDeleteBookingModal}
        show={showDeleteBookingModal}
        showFooter={!isBookingDeleting}
      >
        {!isBookingDeleting ? (
          <>
            {bookingToDeleteInformation && (
              <>
                <div className="mb-4">
                  <JxtEventSummary {...bookingToDeleteInformation.summary} />
                </div>
                <div className="mb-4">
                  <JxtEventDate
                    range={{
                      from: fromISO(bookingToDeleteInformation.start.dateTime),
                      to: fromISO(bookingToDeleteInformation.end.dateTime),
                    }}
                  />
                </div>
                {bookingToDeleteInformation.resourceType?.metaType && (
                  <JxtResourceInfos
                    name={bookingToDeleteInformation.resourceName}
                    type={JxtResourcesTypeEnum[bookingToDeleteInformation.resourceType.metaType]}
                  />
                )}
              </>
            )}
            {bookingToDeleteInformation?.rrule && (
              <>
                <hr className="text-neutral-20 my-4" />
                <form className="flex flex-col gap-3">
                  <h3 className="text-neutral-140 font-semibold">{t('what-event-do-you-want-to-delete')}</h3>
                  <JxtRadioButton
                    value={RecurrentBookingClosingResultEnum.ONLY_SELF}
                    label={t<string>('this-booking-only')}
                    {...register('recurrence')}
                    checked={true}
                  />
                  <JxtRadioButton
                    value={RecurrentBookingClosingResultEnum.FOLLOWING}
                    label={t<string>('this-booking-and-the-following-occurrences')}
                    {...register('recurrence')}
                    checked={false}
                  />
                </form>
              </>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-60">
            <JxtLoader />
          </div>
        )}
      </JxtDeleteEventModal>
    ),
    [
      bookingToDeleteInformation,
      showDeleteBookingModal,
      isBookingDeleting,
      closeDeleteBookingModal,
      handleSubmit,
      onSubmit,
      register,
      t,
    ]
  );

  return {
    checkin,
    checkout,
    arrive,
    leave,
    cancel,
    directCancel,
    approve,
    refuse,
    update,
    create,
    updateParticipationStatus,
    deleteBookingModal,
    deleteBookings,
  };
};
